'use client'

/**
 * SuperviseControl — supervisor entry-point for an interaction.
 *
 * Wraps `CrmApiService.superviseStart/Stop`. The dropdown lets a
 * supervisor pick a mode and join the interaction:
 *   - monitor:  silent listen-only
 *   - whisper:  agent hears supervisor, customer doesn't
 *   - barge:    join as full participant
 *   - takeover: replace the agent on the call
 *
 * The audio/video routing itself (joining the WebRTC bridge) is
 * handled by the call engine when the participant joins; this
 * control is the trigger only. Caller is responsible for gating
 * visibility to supervisors (e.g. via `useMyCrmMember().isSupervisor`).
 */

import { useCallback, useState } from 'react'
import { useTranslations } from '@repo/core/i18n'
import { NexusCrm } from '@open/nexus-public-api'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui'
import { toast } from 'sonner'
import { CallRegular, EyeRegular, HeadphonesRegular, PersonAvailableRegular, SpinnerIosRegular } from '@fluentui/react-icons'

const Mode = NexusCrm.CrmInteractionSuperviseModeNative
type ModeEnum = NexusCrm.CrmInteractionSuperviseModeNative

const MODE_ICON: Record<ModeEnum, typeof EyeRegular> = {
  [Mode.monitor]: EyeRegular,
  [Mode.whisper]: HeadphonesRegular,
  [Mode.barge]: CallRegular,
  [Mode.takeover]: PersonAvailableRegular,
}

interface Props {
  interactionId: string
}

export function SuperviseControl({ interactionId }: Props) {
  const t = useTranslations('crm.supervise')

  const [activeMode, setActiveMode] = useState<ModeEnum | null>(null)
  const [isPending, setIsPending] = useState(false)

  const getApi = useCallback(async () => {
    const { ServiceRegistry, CrmApiService } = await import('@repo/services')
    return new CrmApiService(ServiceRegistry.getMainService())
  }, [])

  const start = useCallback(
    async (mode: ModeEnum) => {
      if (isPending) return
      setIsPending(true)
      try {
        const api = await getApi()
        await api.superviseStart({ interactionId, mode })
        setActiveMode(mode)
        toast.success(t(`toast.started.${mode}`))
      } catch (err) {
        toast.error(t('errors.start'), {
          description: err instanceof Error ? err.message : String(err),
        })
      } finally {
        setIsPending(false)
      }
    },
    [getApi, interactionId, isPending, t],
  )

  const stop = useCallback(async () => {
    if (isPending) return
    setIsPending(true)
    try {
      const api = await getApi()
      await api.superviseStop({ interactionId })
      setActiveMode(null)
      toast.success(t('toast.stopped'))
    } catch (err) {
      toast.error(t('errors.stop'), {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setIsPending(false)
    }
  }, [getApi, interactionId, isPending, t])

  if (activeMode) {
    const Icon = MODE_ICON[activeMode]
    return (
      <Button variant="destructive" size="xs" onClick={() => void stop()} disabled={isPending}>
        {isPending ? (
          <SpinnerIosRegular className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <Icon className="mr-1 h-3 w-3" />
        )}
        {t(`stop.${activeMode}`)}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="xs" disabled={isPending}>
          {isPending ? (
            <SpinnerIosRegular className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <EyeRegular className="mr-1 h-3 w-3" />
          )}
          {t('label')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {Object.values(Mode).map((m) => {
          const Icon = MODE_ICON[m]
          return (
            <DropdownMenuItem key={m} onClick={() => void start(m)}>
              <Icon className="mr-2 h-4 w-4" />
              <div className="flex flex-col">
                <span className="font-medium">{t(`mode.${m}.label`)}</span>
                <span className="text-xs text-muted-foreground">
                  {t(`mode.${m}.description`)}
                </span>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
